<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Tasa de Cambio</h1>
      <v-col v-if="saveSuccess" class="mr-3 ma-0 pa-0 mt-3 mt-md-0 d-flex align-end justify-end">
        <v-alert dense outlined type="success" dismissible>Tasa actualizada correctamente</v-alert>
      </v-col>
      <v-col v-if="saveError" class="mr-3 ma-0 pa-0 mt-3 mt-md-0 d-flex align-end justify-end">
        <v-alert dense outlined type="error">Error al guardar</v-alert>
      </v-col>
    </v-row>

    <!-- Tabla principal: toda moneda con fila en tipo-cambio (CLP, PEN, USD, MXN…); la UF va aparte -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card outlined>
          <v-card-text class="pb-0">
            <p class="text-body-2 grey--text text--darken-2 mb-0">
              Define cuántas unidades de cada moneda equivalen a 1 USD.
              Estos valores se usan para convertir el MRR a USD en todos los reportes.
            </p>
          </v-card-text>

          <v-data-table
            :headers="headers"
            :items="mainRates"
            :loading="loading"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.rate="{ item }">
              <v-text-field
                v-if="editing === item.currency"
                v-model="editValue"
                type="number"
                step="0.01"
                dense
                outlined
                hide-details
                style="max-width: 140px"
                @keyup.enter="saveRate(item)"
                @keyup.esc="editing = null"
              />
              <span v-else>{{ item.rate }}</span>
            </template>

            <template v-slot:item.equivalencia="{ item }">
              <span class="grey--text text-body-2" style="white-space: nowrap">
                {{ item.currency === 'USD' ? '1 USD = 1 USD' : `1 ${item.currency} = ${(1 / item.rate).toFixed(5)} USD` }}
              </span>
            </template>

            <template v-slot:item.actions="{ item }">
              <div v-if="editing === item.currency" class="d-flex gap-1">
                <v-btn small color="primary" :loading="saving" @click="saveRate(item)">
                  <v-icon small>mdi-check</v-icon>
                </v-btn>
                <v-btn small text @click="editing = null">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <v-btn v-else-if="item.currency !== 'USD'" icon small @click="startEdit(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Sección separada: UF -->
    <v-row v-if="ufRate" class="mb-4">
      <v-col cols="12">
        <v-card outlined>
          <v-card-text>
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
              <div>
                <p class="text-body-1 font-weight-medium mb-1">Unidad de Fomento (UF)</p>
                <p class="text-body-2 grey--text text--darken-1 mb-0">
                  Ingresa cuántas UF equivalen a 1 USD.
                  El valor de 1 UF en pesos chilenos (CLP) se calcula automáticamente usando la tasa CLP/USD.
                </p>
              </div>
              <div class="d-flex align-center" style="gap: 12px; flex-shrink: 0;">
                <div v-if="editingUf" class="d-flex align-center" style="gap: 8px;">
                  <v-text-field
                    v-model="editValueUf"
                    type="number"
                    step="0.00001"
                    dense
                    outlined
                    hide-details
                    prefix="1 USD ="
                    suffix="UF"
                    style="max-width: 220px"
                    @keyup.enter="saveUfRate"
                    @keyup.esc="editingUf = false"
                  />
                  <v-btn small color="primary" :loading="saving" @click="saveUfRate">
                    <v-icon small>mdi-check</v-icon>
                  </v-btn>
                  <v-btn small text @click="editingUf = false">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
                <div v-else class="d-flex align-center" style="gap: 16px;">
                  <div class="text-right">
                    <div class="text-body-2 grey--text">1 UF = <strong>{{ ufInClp ? ufInClp.toLocaleString('es-CL', { maximumFractionDigits: 0 }) + ' CLP' : '...' }}</strong></div>
                    <div class="text-body-2 grey--text text--lighten-1">
                      1 USD = {{ ufRate.rate }} UF
                    </div>
                  </div>
                  <v-btn icon small @click="startEditUf">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import TipoCambioService from '../services/tipo-cambio.service';

export default {
  name: 'TipoCambio',
  data() {
    return {
      loading: false,
      saving: false,
      saveSuccess: false,
      saveError: false,
      editing: null,
      editValue: null,
      editingUf: false,
      editValueUf: null,
      rates: [],
      headers: [
        { text: 'Moneda', value: 'currency', sortable: false },
        { text: 'Unidades por 1 USD', value: 'rate', sortable: false, width: '160px' },
        { text: 'Equivalencia', value: 'equivalencia', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
    };
  },
  computed: {
    mainRates() {
      return this.rates.filter(r => r.currency !== 'UF');
    },
    ufRate() {
      return this.rates.find(r => r.currency === 'UF') ?? null;
    },
    clpRate() {
      return this.rates.find(r => r.currency === 'CLP')?.rate ?? null;
    },
    ufInClp() {
      // UF se guarda como "UF por 1 USD" (ej: 0.231 UF).
      // 1 UF en CLP = (CLP por USD) / (UF por USD).
      if (!this.ufRate?.rate || !this.clpRate) return null;
      return this.clpRate / this.ufRate.rate;
    },
  },
  mounted() {
    this.loadRates();
  },
  methods: {
    async loadRates() {
      this.loading = true;
      const res = await TipoCambioService.getAll();
      if (res.status === 'ok') this.rates = res.data;
      this.loading = false;
    },
    startEdit(item) {
      this.editing = item.currency;
      this.editValue = item.rate;
      this.saveSuccess = false;
      this.saveError = false;
    },
    startEditUf() {
      this.editingUf = true;
      this.editValueUf = this.ufRate?.rate;
      this.saveSuccess = false;
      this.saveError = false;
    },
    async saveRate(item) {
      this.saving = true;
      const res = await TipoCambioService.updateRate(item.currency, parseFloat(this.editValue));
      if (res.status === 'ok') {
        item.rate = parseFloat(this.editValue);
        this.saveSuccess = true;
        setTimeout(() => { this.saveSuccess = false; }, 3000);
      } else {
        this.saveError = true;
        setTimeout(() => { this.saveError = false; }, 3000);
      }
      this.editing = null;
      this.saving = false;
    },
    async saveUfRate() {
      this.saving = true;
      const newValue = parseFloat(this.editValueUf);
      const res = await TipoCambioService.updateRate('UF', newValue);
      if (res.status === 'ok') {
        this.ufRate.rate = newValue;
        this.saveSuccess = true;
        setTimeout(() => { this.saveSuccess = false; }, 3000);
      } else {
        this.saveError = true;
        setTimeout(() => { this.saveError = false; }, 3000);
      }
      this.editingUf = false;
      this.saving = false;
    },
  },
};
</script>
